import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { track, getSessionId, getUTM } from "../lib/analytics";
import { BrandLogo, LanguageSwitcher } from "../components/SiteChrome";
import { useLang } from "../context/LanguageContext";

const PROGRESS_KEY = "mhc_progress_v1";

const OPTION_STYLES = {
  yes: { ring: "#2E9B4B", bg: "rgba(46,155,75,0.08)" },
  partial: { ring: "#F2A900", bg: "rgba(242,169,0,0.08)" },
  no: { ring: "#D64545", bg: "rgba(214,69,69,0.08)" },
  na: { ring: "#94A3B8", bg: "rgba(148,163,184,0.10)" },
};

export default function Assessment() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [dir, setDir] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { lang, t } = useLang();

  // load questions + restore progress
  useEffect(() => {
    api.get("/questions").then((res) => {
      setQuestions(res.data.questions);
      try {
        const saved = JSON.parse(localStorage.getItem(PROGRESS_KEY) || "null");
        if (saved && saved.answers) {
          setAnswers(saved.answers);
          setIndex(Math.min(saved.index || 0, res.data.questions.length - 1));
        }
      } catch (e) { /* ignore */ }
      setLoaded(true);
    });
  }, []);

  // persist progress
  useEffect(() => {
    if (loaded) localStorage.setItem(PROGRESS_KEY, JSON.stringify({ answers, index }));
  }, [answers, index, loaded]);

  const total = questions.length;
  const q = questions[index];
  const suf = lang === "en" ? "" : `_${lang}`;
  const qText = q ? (q[`text${suf}`] || q.text) : "";
  const qOverline = q ? (q[`overline${suf}`] || q.overline) : "";
  const optLoc = q && suf && q[`options${suf}`]
    ? Object.fromEntries(q[`options${suf}`].map((o) => [o.key, o])) : {};
  const optText = (opt) => (optLoc[opt.key] ? optLoc[opt.key] : opt);
  const progress = total ? Math.round(((index + (answers[q?.code] ? 1 : 0)) / total) * 100) : 0;
  const selected = q ? answers[q.code] : null;

  const select = (optKey) => {
    setAnswers((a) => ({ ...a, [q.code]: optKey }));
    track("question_answered", { code: q.code, answer: optKey, index: index + 1 });
  };

  const next = () => {
    if (!selected) { toast.error("Please select an option to continue."); return; }
    if (index < total - 1) { setDir(1); setIndex((i) => i + 1); }
    else submit();
  };
  const back = () => { if (index > 0) { setDir(-1); setIndex((i) => i - 1); } };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post("/assessments", {
        answers, session_id: getSessionId(), utm: getUTM(),
      });
      track("assessment_completed", { score: res.data.report.score });
      localStorage.removeItem(PROGRESS_KEY);
      navigate(`/result/${res.data.assessment_id}`, { state: { report: res.data.report } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit. Try again.");
      setSubmitting(false);
    }
  };

  const variants = {
    enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
  };

  if (!loaded || !q) {
    return <div className="min-h-screen grid place-items-center text-navy"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* top bar */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
          <BrandLogo />
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button onClick={() => navigate("/")} className="text-xs text-slate-400 hover:text-navy transition-colors duration-200">{t("exit")}</button>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-5 pb-3">
          <div className="flex justify-between text-xs font-semibold text-slate-500 mb-1.5">
            <span data-testid="question-counter">{t("question_of", { n: index + 1, t: total })}</span>
            <span data-testid="progress-percent">{t("percent_complete", { p: progress })}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full" style={{ width: `${progress}%`, background: "linear-gradient(90deg,#123D73,#E9A21A)", transition: "width 0.5s ease-out" }} />
          </div>
        </div>
      </div>

      {/* question */}
      <div className="flex-1 max-w-2xl w-full mx-auto px-5 py-8 md:py-12 pb-32">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div key={q.code} custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.28, ease: "easeOut" }}>
            <div className="text-xs tracking-[0.2em] uppercase text-gold font-bold" data-testid="question-overline">
              {qOverline}
            </div>
            <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-slate-400 font-semibold">
              {t("question_of", { n: String(index + 1).padStart(2, "0"), t: total })}
            </div>
            <h1 className="mt-4 font-heading font-extrabold text-navy text-2xl md:text-3xl tracking-tight leading-snug" data-testid="question-text">
              {qText}
            </h1>

            <div className="mt-8 space-y-3.5">
              {q.options.map((opt) => {
                const active = selected === opt.key;
                const st = OPTION_STYLES[opt.key] || OPTION_STYLES.na;
                const ot = optText(opt);
                return (
                  <button
                    key={opt.key}
                    data-testid={`option-${opt.key}`}
                    onClick={() => select(opt.key)}
                    className="w-full text-left rounded-2xl border p-5 flex items-start gap-4 transition-transform duration-200 hover:-translate-y-0.5"
                    style={{
                      borderColor: active ? st.ring : "#E2E8F0",
                      background: active ? st.bg : "#fff",
                      boxShadow: active ? `0 0 0 2px ${st.ring}` : "0 1px 2px rgba(6,27,58,0.04)",
                    }}
                  >
                    <div className="mt-0.5 grid place-items-center h-6 w-6 rounded-full shrink-0 border-2"
                      style={{ borderColor: active ? st.ring : "#CBD5E1", background: active ? st.ring : "transparent" }}>
                      {active && <Check size={14} className="text-white" strokeWidth={3} />}
                    </div>
                    <div>
                      <div className="font-heading font-bold text-navy">{ot.label}</div>
                      <div className="text-sm text-slate-500 leading-relaxed mt-0.5">{ot.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* sticky nav */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
          <button data-testid="back-btn" onClick={back} disabled={index === 0}
            className="rounded-full border border-slate-200 px-6 py-3 font-heading font-semibold text-navy flex items-center gap-2 disabled:opacity-40 hover:bg-slate-50 transition-colors duration-200">
            <ArrowLeft size={18} /> {t("back")}
          </button>
          <button data-testid="next-btn" onClick={next} disabled={submitting}
            className="flex-1 rounded-full bg-navy text-white font-heading font-bold px-6 py-3 flex items-center justify-center gap-2 hover:bg-royal transition-colors duration-300 disabled:opacity-60">
            {submitting ? <Loader2 className="animate-spin" size={18} /> : (
              <>{index === total - 1 ? t("see_results") : t("next")} <ArrowRight size={18} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
